import { useState, useCallback } from 'react';

export const useApiWithRetry = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const callApi = useCallback(async (apiFunction, maxRetries = 2) => {
    let retries = 0;
    setLoading(true);
    setError(null);

    while (retries <= maxRetries) {
      try {
        const result = await apiFunction();
        setLoading(false);
        return result;
      } catch (err) {
        console.error(`API call failed (attempt ${retries + 1}):`, err);
        
        // Check if it's a 401 error and we have retries left
        if (err.response?.status === 401 && retries < maxRetries) {
          // Wait a bit for the token refresh to happen
          await new Promise(resolve => setTimeout(resolve, 1000));
          retries++;
          continue;
        }
        
        // Check if it's a network error and we have retries left
        if (!err.response && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          retries++;
          continue;
        }
        
        // All retries exhausted or non-retryable error
        setError(err.message || 'An error occurred');
        setLoading(false);
        throw err;
      }
    }
  }, []);

  return { callApi, loading, error };
};

// Fetch wrapper with retry logic for non-axios requests
export const fetchWithRetry = async (url, options = {}, maxRetries = 2) => {
  let retries = 0;
  
  while (retries <= maxRetries) {
    try {
      const response = await fetch(url, options);
      
      // Don't retry on 401 - let the interceptor handle auth
      if (response.status === 401) {
        return response;
      }
      
      // Retry on 5xx errors or network issues
      if (response.status >= 500 && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        retries++;
        continue;
      }
      
      return response;
    } catch (err) {
      // Network error - retry if we have attempts left
      if (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        retries++;
        continue;
      }
      throw err;
    }
  }
};